import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Users, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { queryKeys } from "@/constants/queryKeys";
import {
  createChatConversation,
  fetchChatConversations,
  fetchChatDirectory,
} from "@/services/api/chat";
import { useMessagesStore } from "../store/useMessagesStore";
import { ConversationAvatar } from "./ConversationAvatar";
import { formatChatTime } from "../lib/format";
import type { ChatUser } from "../data/types";

type ConversationListProps = {
  onSelectConversation?: () => void;
};

export function ConversationList({ onSelectConversation }: ConversationListProps) {
  const search = useMessagesStore((s) => s.search);
  const setSearch = useMessagesStore((s) => s.setSearch);
  const activeConversationId = useMessagesStore((s) => s.activeConversationId);
  const setActiveConversationId = useMessagesStore((s) => s.setActiveConversationId);
  const composerMode = useMessagesStore((s) => s.composerMode);
  const setComposerMode = useMessagesStore((s) => s.setComposerMode);

  const { data: conversations = [], isLoading, isError } = useQuery({
    queryKey: queryKeys.chatConversations(search),
    queryFn: () => fetchChatConversations({ q: search || undefined }),
  });

  return (
    <aside className="relative flex h-full min-h-0 w-full min-w-0 flex-col border-r border-border bg-surface lg:w-[280px] lg:max-w-[280px] lg:shrink-0">
      <div className="shrink-0 space-y-3 border-b border-border p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-fg">Messages</h2>
          <button
            type="button"
            aria-label="New chat"
            onClick={() => setComposerMode(composerMode === "idle" ? "dm" : "idle")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-[6px] border border-border text-fg-muted hover:bg-surface-muted"
          >
            {composerMode === "idle" ? <Plus className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search messages..."
            className="h-10 w-full rounded-[6px] border border-border bg-surface-muted pr-3 pl-9 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30"
          />
        </div>
      </div>

      {composerMode !== "idle" ? (
        <NewChatComposer
          mode={composerMode}
          onModeChange={setComposerMode}
          onCreated={(id) => {
            setActiveConversationId(id);
            setComposerMode("idle");
            onSelectConversation?.();
          }}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <p className="px-3 py-6 text-sm text-fg-muted">Loading conversations…</p>
          ) : isError ? (
            <p className="px-3 py-6 text-sm text-fg-muted">Could not load conversations.</p>
          ) : conversations.length === 0 ? (
            <p className="px-3 py-6 text-sm text-fg-muted">
              No chats yet. Tap + to start a DM or group.
            </p>
          ) : (
            <ul className="space-y-1">
              {conversations.map((item) => {
                const active = item.id === activeConversationId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveConversationId(item.id);
                        onSelectConversation?.();
                      }}
                      className={cn(
                        "flex w-full items-start gap-3 rounded-[6px] px-3 py-3 text-left transition-colors",
                        active
                          ? "bg-papaya-leaf/10 dark:bg-papaya/10"
                          : "hover:bg-surface-muted",
                      )}
                    >
                      <ConversationAvatar conversation={item} />
                      <span className="min-w-0 flex-1">
                        <span className="mb-0.5 flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-fg">
                            {item.type === "group" ? (
                              <span className="inline-flex items-center gap-1">
                                <Users className="h-3.5 w-3.5 text-fg-subtle" />
                                {item.name}
                              </span>
                            ) : (
                              item.name
                            )}
                          </span>
                          <span className="shrink-0 text-[11px] text-fg-subtle">
                            {formatChatTime(item.last_message_at || item.time)}
                          </span>
                        </span>
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs text-fg-muted">
                            {item.preview || "No messages yet"}
                          </span>
                          {item.unread ? (
                            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-papaya-leaf px-1.5 text-[10px] font-semibold text-white">
                              {item.unread}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </aside>
  );
}

function NewChatComposer({
  mode,
  onModeChange,
  onCreated,
}: {
  mode: "dm" | "group";
  onModeChange: (mode: "idle" | "dm" | "group") => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<ChatUser[]>([]);

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.chatDirectory(q),
    queryFn: () => fetchChatDirectory(q),
  });

  const createMutation = useMutation({
    mutationFn: createChatConversation,
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      onCreated(conversation.id);
    },
  });

  const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

  const toggleUser = (user: ChatUser) => {
    if (mode === "dm") {
      createMutation.mutate({ type: "dm", peer_user_id: user.id });
      return;
    }
    setSelected((prev) =>
      selectedIds.has(user.id) ? prev.filter((u) => u.id !== user.id) : [...prev, user],
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex gap-2 border-b border-border p-3">
        <button
          type="button"
          onClick={() => onModeChange("dm")}
          className={cn(
            "flex-1 rounded-[6px] px-2 py-1.5 text-xs font-medium",
            mode === "dm" ? "bg-papaya-leaf text-white" : "bg-surface-muted text-fg-muted",
          )}
        >
          Direct
        </button>
        <button
          type="button"
          onClick={() => onModeChange("group")}
          className={cn(
            "flex-1 rounded-[6px] px-2 py-1.5 text-xs font-medium",
            mode === "group" ? "bg-papaya-leaf text-white" : "bg-surface-muted text-fg-muted",
          )}
        >
          Group
        </button>
      </div>

      {mode === "group" ? (
        <div className="space-y-2 border-b border-border p-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Group name"
            className="h-9 w-full rounded-[6px] border border-border bg-surface-muted px-3 text-sm outline-none"
          />
          <button
            type="button"
            disabled={!title.trim() || selected.length === 0 || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                type: "group",
                title: title.trim(),
                member_ids: selected.map((u) => u.id),
              })
            }
            className="h-9 w-full rounded-[6px] bg-papaya-leaf text-sm font-medium text-white disabled:opacity-50"
          >
            Create group
          </button>
        </div>
      ) : null}

      <div className="border-b border-border p-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search people…"
          className="h-9 w-full rounded-[6px] border border-border bg-surface-muted px-3 text-sm outline-none"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <p className="px-3 py-4 text-sm text-fg-muted">Loading…</p>
        ) : (
          <ul className="space-y-1">
            {users.map((user) => {
              const active = selectedIds.has(user.id);
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => toggleUser(user)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-[6px] px-3 py-2.5 text-left hover:bg-surface-muted",
                      active && "bg-papaya-leaf/10",
                    )}
                  >
                    <ConversationAvatar
                      conversation={{
                        name: user.name || user.username,
                        initials: user.initials,
                        tone: "leaf",
                        online: user.online || user.is_online,
                      }}
                      size="sm"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-fg">
                        {user.name || user.username}
                      </span>
                      <span className="block truncate text-xs text-fg-muted">
                        {user.extension || user.username}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {createMutation.isError ? (
          <p className="px-3 py-2 text-xs text-red-600">Could not create chat.</p>
        ) : null}
      </div>
    </div>
  );
}
